import { Link } from '@inertiajs/react'
import { useMemo, useState } from 'react'

import AdminLayout from '../../../components/AdminLayout'
import { AdminCard, AdminHead } from '../../../components/admin/AdminHead'
import { OrderStatus } from '../../../components/admin/OrderStatus'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Order {
  id: number
  status: string
  statusLabel: string
  customerName: string
  customerEmail: string
  total: number
  itemCount: number
  createdAt: string
}

interface Props {
  orders: Order[]
}

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'shipped', label: 'Shipped' },
]

/**
 * Spec 11 · The screen the admin section is drawn from: filter chips
 * and a search field over a hairline table, with status pills in the
 * status column. Accent is reserved for the active filter.
 */
export default function AdminOrdersIndex({ orders }: Props) {
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return orders.filter((order) => {
      if (filter !== 'all' && order.status !== filter) return false
      if (!needle) return true
      return (
        order.customerName.toLowerCase().includes(needle) ||
        order.customerEmail.toLowerCase().includes(needle) ||
        String(order.id).includes(needle)
      )
    })
  }, [orders, filter, query])

  return (
    <AdminLayout>
      <AdminHead eyebrow="Fulfilment" title="Orders" sub={`${orders.length} total`} />

      <AdminCard>
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-[18px] py-3.5">
          <ToggleGroup
            type="single"
            look="chips"
            value={filter}
            onValueChange={(value) => value && setFilter(value)}
            aria-label="Filter orders by status"
          >
            {FILTERS.map((option) => (
              <ToggleGroupItem key={option.value} look="chips" value={option.value}>
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search orders"
            aria-label="Search orders"
            className="ml-auto h-9 w-50 text-[12px] md:h-9"
          />
        </div>

        {shown.length === 0 ? (
          <div className="p-[18px]">
            <EmptyState
              title={orders.length === 0 ? 'No orders yet.' : 'No orders match those filters.'}
            />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-surface-sunk">
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Placed</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((order) => (
                // Pending orders are the ones owed work, so they take
                // the accent wash.
                <TableRow key={order.id} flagged={order.status === 'pending'}>
                  <TableCell className="tabular-nums">
                    #SS-{String(order.id).padStart(4, '0')}
                  </TableCell>
                  <TableCell>
                    <div>{order.customerName}</div>
                    <div className="text-[11px] text-ink-muted">{order.customerEmail}</div>
                  </TableCell>
                  <TableCell>
                    <OrderStatus status={order.status} label={order.statusLabel} />
                  </TableCell>
                  <TableCell className="text-ink-muted">{order.itemCount} items</TableCell>
                  <TableCell className="text-ink-muted">{order.createdAt}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    ${order.total.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      href={`/admin/orders/${order.id}`}
                      className="text-[13px] font-medium underline underline-offset-4 hover:text-signal-deep"
                    >
                      View
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="flex items-center justify-between gap-4 px-[18px] py-3 text-[11px] text-ink-muted">
          <span>
            {shown.length} of {orders.length}
          </span>
        </div>
      </AdminCard>
    </AdminLayout>
  )
}
